import { useEffect, useState } from "react";
import type { VyllageWorldElement } from "../world-engine";

/**
 * Zoom controls, rendered by React instead of injected by the engine. The engine
 * skips its own `.vy-zoom` chrome when the host sets `data-react-zoom` (see
 * VyllageWorldHost); we drive its public zoom API and poll the live zoom for the %.
 * Sits under the engine's minimap (top-right).
 */
export function SceneControls({
  worldRef,
}: {
  worldRef: React.RefObject<VyllageWorldElement | null>;
}) {
  const [pct, setPct] = useState(100);

  useEffect(() => {
    let raf = 0;
    let last = -1;
    const tick = () => {
      const z = worldRef.current?.getUserZoom?.() ?? 1;
      const next = Math.round(z * 100);
      if (next !== last) {
        last = next;
        setPct(next);
      }
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [worldRef]);

  const btn = (label: string, title: string, fn: () => void) => (
    <button
      type="button"
      title={title}
      onPointerDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        fn();
      }}
      className="flex h-[34px] w-[34px] cursor-pointer items-center justify-center rounded-[10px] border border-[var(--vy-accent-border)] bg-[var(--vy-bar-deep)]/90 font-bold text-white leading-none shadow-[0_4px_12px_rgba(0,24,71,0.3)] hover:border-[var(--vy-accent)] hover:bg-[var(--vy-bar)]"
      style={{ fontFamily: "var(--font-display)", fontSize: 16 }}
    >
      {label}
    </button>
  );

  return (
    <div className="pointer-events-auto absolute right-3 top-[158px] z-30 flex flex-col gap-1">
      {btn("+", "Zoom in (+ key)", () => worldRef.current?.zoomIn())}
      <div
        className="py-0.5 text-center font-bold tracking-[0.06em] text-white/70"
        style={{ fontFamily: "var(--font-display)", fontSize: 9 }}
      >
        {pct}%
      </div>
      {btn("−", "Zoom out (− key)", () => worldRef.current?.zoomOut())}
      {btn("⟲", "Reset zoom (0)", () => worldRef.current?.resetZoom())}
    </div>
  );
}
